import { makeAutoObservable } from 'mobx'

export type ChatToolCall = {
    name?: string
    input?: Record<string, unknown>
    result?: Record<string, unknown>
}

export type ChatMessage = {
    role: 'user' | 'assistant'
    content: string
    toolCalls?: ChatToolCall[]
}

export type QueuedChatMessage = {
    id: string
    content: string
}

export type ChatConversationSummary = {
    id: string
    title: string
    model?: string
    status?: 'queued' | 'running' | 'succeeded' | 'failed' | string
    createdAt?: string
    updatedAt?: string
    toolCallCount?: number
}

export const initialChatMessages: ChatMessage[] = [
    {
        role: 'assistant',
        content:
            'Ask me about your Amazon ads performance, recipes, bulk actions, or account data.',
    },
]

type PersistedChatInput = {
    draft?: string
    queuedMessages?: QueuedChatMessage[]
    queuedMessageId?: number
}

const storagePrefix = 'merchjar:chat'

const parsePersistedInput = (value: string | null): PersistedChatInput => {
    if (!value) return {}

    try {
        const parsed = JSON.parse(value) as PersistedChatInput
        return {
            draft: typeof parsed.draft === 'string' ? parsed.draft : '',
            queuedMessageId:
                typeof parsed.queuedMessageId === 'number' &&
                Number.isFinite(parsed.queuedMessageId)
                    ? parsed.queuedMessageId
                    : 0,
            queuedMessages: Array.isArray(parsed.queuedMessages)
                ? parsed.queuedMessages.filter(
                      (message): message is QueuedChatMessage =>
                          typeof message.id === 'string' && typeof message.content === 'string',
                  )
                : [],
        }
    } catch {
        return {}
    }
}

class ChatStore {
    private inputPersistenceTimer: ReturnType<typeof setTimeout> | null = null

    routeConversationId: string | null = null
    messages: ChatMessage[] = [...initialChatMessages]
    draft = ''
    isSending = false
    queuedMessages: QueuedChatMessage[] = []
    error: string | null = null
    conversations: ChatConversationSummary[] = []
    currentRunId: string | null = null
    currentTitle = 'New conversation'
    isLoadingConversations = false
    selectedModel = 'default'
    conversationSearch = ''
    queuedMessageId = 0

    constructor() {
        makeAutoObservable(this, {
            flushPersistedInput: false,
            persistInput: false,
            scheduleInputPersistence: false,
        })
    }

    get hasActiveConversation(): boolean {
        return (
            this.currentRunId !== null ||
            this.isSending ||
            this.messages.length > initialChatMessages.length
        )
    }

    get persistenceConversationId(): string | null {
        return this.routeConversationId ?? this.currentRunId
    }

    storageKey(conversationId: string | null = this.persistenceConversationId): string {
        return [storagePrefix, conversationId ?? 'new'].join(':')
    }

    setRouteContext(routeConversationId: string | null): void {
        this.flushPersistedInput()
        this.routeConversationId = routeConversationId
        this.hydratePersistedInput(routeConversationId)
    }

    hydratePersistedInput(conversationId: string | null = this.persistenceConversationId): void {
        const persisted = parsePersistedInput(localStorage.getItem(this.storageKey(conversationId)))
        this.draft = persisted.draft ?? ''
        this.queuedMessages = persisted.queuedMessages ?? []
        this.queuedMessageId = Math.max(
            persisted.queuedMessageId ?? 0,
            ...this.queuedMessages
                .map((message) => Number(message.id.replace(/^queued-message-/, '')))
                .filter((value) => Number.isFinite(value)),
            0,
        )
    }

    persistInput(conversationId: string | null = this.persistenceConversationId): void {
        if (this.inputPersistenceTimer) {
            clearTimeout(this.inputPersistenceTimer)
            this.inputPersistenceTimer = null
        }

        const key = this.storageKey(conversationId)

        if (!this.draft.trim() && this.queuedMessages.length === 0) {
            localStorage.removeItem(key)
            return
        }

        const persistedInput: PersistedChatInput = {
            draft: this.draft,
            queuedMessageId: this.queuedMessageId,
            queuedMessages: this.queuedMessages,
        }

        localStorage.setItem(key, JSON.stringify(persistedInput))
    }

    scheduleInputPersistence(): void {
        if (this.inputPersistenceTimer) clearTimeout(this.inputPersistenceTimer)

        this.inputPersistenceTimer = setTimeout(() => {
            this.inputPersistenceTimer = null
            this.persistInput()
        }, 250)
    }

    flushPersistedInput(): void {
        this.persistInput()
    }

    transferPersistedInput(
        fromConversationId: string | null,
        toConversationId: string | null,
    ): void {
        const fromKey = this.storageKey(fromConversationId)
        const toKey = this.storageKey(toConversationId)

        if (fromKey !== toKey && localStorage.getItem(fromKey)) {
            localStorage.setItem(toKey, localStorage.getItem(fromKey) ?? '')
            localStorage.removeItem(fromKey)
        }

        this.persistInput(toConversationId)
    }

    setMessages(messages: ChatMessage[]): void {
        this.messages = messages
    }

    setDraft(draft: string): void {
        this.draft = draft
        this.scheduleInputPersistence()
    }

    clearDraft(): void {
        this.draft = ''
        this.persistInput()
    }

    setIsSending(isSending: boolean): void {
        this.isSending = isSending
    }

    setError(error: string | null): void {
        this.error = error
    }

    setConversations(conversations: ChatConversationSummary[]): void {
        this.conversations = conversations
    }

    setIsLoadingConversations(isLoadingConversations: boolean): void {
        this.isLoadingConversations = isLoadingConversations
    }

    setSelectedModel(selectedModel: string): void {
        this.selectedModel = selectedModel
    }

    setConversationSearch(conversationSearch: string): void {
        this.conversationSearch = conversationSearch
    }

    setCurrentRunId(
        currentRunId: string | null,
        options: { transferPersistedInput?: boolean } = {},
    ): void {
        const previousConversationId = this.persistenceConversationId
        this.currentRunId = currentRunId
        this.routeConversationId = currentRunId

        if (currentRunId && options.transferPersistedInput) {
            this.transferPersistedInput(previousConversationId, currentRunId)
        } else if (!currentRunId) {
            this.persistInput(null)
        }
    }

    setCurrentTitle(currentTitle: string): void {
        this.currentTitle = currentTitle
    }

    addQueuedMessage(content: string): void {
        this.queuedMessageId += 1
        this.queuedMessages = [
            ...this.queuedMessages,
            {
                id: `queued-message-${String(this.queuedMessageId)}`,
                content,
            },
        ]
        this.clearDraft()
        this.persistInput()
    }

    cancelQueuedMessage(id: string): void {
        this.queuedMessages = this.queuedMessages.filter((message) => message.id !== id)
        this.persistInput()
    }

    shiftQueuedMessage(id: string): void {
        this.queuedMessages =
            this.queuedMessages[0]?.id === id ? this.queuedMessages.slice(1) : this.queuedMessages
        this.persistInput()
    }

    removeConversation(id: string): void {
        this.conversations = this.conversations.filter((conversation) => conversation.id !== id)
    }

    updateConversation(conversation: ChatConversationSummary): void {
        this.conversations = this.conversations.map((item) =>
            item.id === conversation.id ? conversation : item,
        )
    }

    resetActiveConversation(options: { clearPersistedInput?: boolean } = {}): void {
        if (this.inputPersistenceTimer) {
            clearTimeout(this.inputPersistenceTimer)
            this.inputPersistenceTimer = null
        }

        if (options.clearPersistedInput ?? true) {
            localStorage.removeItem(this.storageKey())
        }

        this.messages = [...initialChatMessages]
        this.draft = ''
        this.isSending = false
        this.queuedMessages = []
        this.error = null
        this.currentRunId = null
        this.routeConversationId = null
        this.currentTitle = 'New conversation'
        this.queuedMessageId = 0
    }
}

export const chatStore = new ChatStore()
